import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import audioThumbnailLight from "../../../assets/audio_thumbnail_light.svg";
import audioThumbnailDark from "../../../assets/audio_thumbnail_dark.svg";

interface SpeakerLineClipProps {
  /** Full media URL — the clip seeks within it rather than loading a cut. */
  src: string;
  isAudio: boolean;
  /** Line bounds, in seconds. Playback is confined to [start, end]. */
  start: number;
  end: number;
  /** Square-ish preview edge in px. */
  size?: number;
}

/**
 * A tiny scrubber-free player bound to one transcript line: it seeks the shared
 * media file to the line's start, plays until its end, then rewinds — so the
 * reviewer can hear/see just that line to identify the speaker. Metadata-only
 * preload keeps many clips cheap; the frame at `start` is shown as the poster.
 */
export const SpeakerLineClip: React.FC<SpeakerLineClipProps> = ({
  src,
  isAudio,
  start,
  end,
  size = 132,
}) => {
  const theme = useTheme();
  const mediaRef = useRef<HTMLVideoElement>(null);
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioPoster =
    theme.palette.mode === "dark" ? audioThumbnailDark : audioThumbnailLight;

  const clearEndTimer = () => {
    if (endTimerRef.current !== null) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
  };

  // Stop at the line's end and rewind so the next play starts clean.
  const stopAtEnd = () => {
    const el = mediaRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = start;
  };

  // Park the playhead on the line's first frame so the poster is representative.
  const seekToStart = () => {
    const el = mediaRef.current;
    if (el) el.currentTime = start;
  };

  const toggle = () => {
    const el = mediaRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
      return;
    }
    // Always (re)start from the line's beginning.
    if (el.currentTime < start || el.currentTime >= end) el.currentTime = start;
    void el.play();
  };

  // Schedule a precise stop at the end boundary: onTimeUpdate fires only a few
  // times per second, so on its own it can overshoot `end` noticeably.
  const handlePlay = () => {
    setIsPlaying(true);
    clearEndTimer();
    const el = mediaRef.current;
    if (!el) return;
    const rate = el.playbackRate || 1;
    const remainingMs = ((end - el.currentTime) / rate) * 1000;
    endTimerRef.current = setTimeout(stopAtEnd, Math.max(remainingMs, 0));
  };

  const handlePause = () => {
    setIsPlaying(false);
    clearEndTimer();
  };

  // Fallback boundary check in case the timer drifts or fails to fire.
  const handleTimeUpdate = () => {
    const el = mediaRef.current;
    if (el && el.currentTime >= end) stopAtEnd();
  };

  // Ensure any pending stop timer is cancelled when the clip unmounts.
  useEffect(() => clearEndTimer, []);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        aspectRatio: "16 / 9",
        flexShrink: 0,
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: theme.palette.common.black,
      }}
    >
      <video
        ref={mediaRef}
        src={src}
        poster={isAudio ? audioPoster : undefined}
        preload="metadata"
        playsInline
        onLoadedMetadata={seekToStart}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handlePause}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
      <IconButton
        aria-label={isPlaying ? "Pause line clip" : "Play line clip"}
        onClick={toggle}
        size="small"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          color: theme.palette.common.white,
          backgroundColor: "rgba(0, 0, 0, 0.55)",
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}
      >
        {isPlaying ? (
          <PauseIcon fontSize="small" />
        ) : (
          <PlayArrowIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
};
